import { ActionIcon, Avatar, Badge, Tooltip } from '@mantine/core';
import { IconBug, IconMinus, IconPhoto, IconPlus, IconStrikethrough } from '@tabler/icons-react';
import { clsx } from 'clsx';
import { useAtomValue, useSetAtom } from 'jotai';
import { useMemo, useState } from 'react';
import { adminUiHiddenAtom, screenshotDiffAtom } from '~/atoms/atoms';
import { useAuthData } from '~/hooks/auth/use-auth-data';
import { api } from '~/trpc-extra/api';
import type { DiffType } from '~/common/array/findDiffs';
import type { SelectedOperation } from '~/server/logic/prisma/operation-select';
import type { SelectedStep } from '~/server/logic/prisma/step-select';
import type { AiActionLog } from '~/types';
import OperationInner from '../steps/step/operation/OperationInner';
import AiActionLogDisplay from '~/components/testRun/ExecutionView/AiActionLogDisplay';

export default function OperationDiff({
  step,
  operation,
  type,
  selectScreenshot,
  testRunAttemptId,
  aiActionLogs = [],
}: {
  step: SelectedStep;
  operation?: SelectedOperation;
  type: DiffType;
  selectScreenshot?: () => void;
  testRunAttemptId?: string;
  aiActionLogs?: AiActionLog[];
}) {
  const selectedScreenshotDiff = useAtomValue(screenshotDiffAtom);
  const setScreenshotDiff = useSetAtom(screenshotDiffAtom);
  const adminUiHidden = useAtomValue(adminUiHiddenAtom);
  const { isAdmin } = useAuthData();
  const { mutateAsync: getLangSmithAiTraceUrl } = api.admin.getLangSmithAiTraceUrl.useMutation();
  const [selectedAiLogId, setSelectedAiLogId] = useState<string | undefined>();

  const isSelected =
    (operation?.id === selectedScreenshotDiff.newOperationId ||
      operation?.id === selectedScreenshotDiff.oldOperationId) &&
    selectedScreenshotDiff.opened;

  const showLangSmithDebug =
    !!operation && operation.type === 'AI_ACTION' && !!testRunAttemptId && isAdmin && !adminUiHidden;

  // Filter logs for this operation - memoized to avoid recalculation on each render
  const filteredAiActionLogs = useMemo(
    () => aiActionLogs.filter((log) => log.parentOperationId === operation?.id),
    [aiActionLogs, operation?.id],
  );
  const hasAiActionLogs = operation?.type === 'AI_ACTION' && filteredAiActionLogs.length > 0;

  return operation ?
      <div className="flex flex-col">
        <div
          className={clsx('flex h-full flex-grow items-center rounded-lg border-2', {
            'opacity-60': operation.skip,
            'bg-red-100 dark:bg-red-900/20': type === 'deleted',
            'bg-emerald-100 dark:bg-green-900/20': type === 'added',
            'border-blue-500': isSelected,
            'border-transparent': !isSelected, // hack to avoid height shifting when the operation is selected
            'rounded-b-none': hasAiActionLogs, // Remove bottom rounding when AI action logs are shown
          })}
        >
          <div className="flex w-full items-center justify-between">
            {operation.pageAlias !== 'page' && (
              <Badge
                size="xs"
                className="mx-1 min-w-fit lowercase"
                color="gray"
              >
                {operation.pageAlias}
              </Badge>
            )}
            {!!operation.skip && (
              <div className="mx-2">
                <Tooltip label="Operation was skipped">
                  <Avatar
                    size="sm"
                    radius="xl"
                    className="mr-1"
                  >
                    <IconStrikethrough size={16} />
                  </Avatar>
                </Tooltip>
              </div>
            )}

            {type === 'deleted' ?
              <IconMinus
                size={14}
                className="mx-2 text-red-700 dark:text-red-400"
              />
            : type === 'added' ?
              <IconPlus
                size={14}
                className="mx-2 text-emerald-700 dark:text-green-400"
              />
            : <IconPlus
                size={14}
                className="invisible mx-2"
              />
            }

            <div className="flex w-full flex-col">
              <OperationInner
                operation={operation}
                step={step}
                previewCursorAllowed={false}
                stepIdx={step.stepOrder} // This may want to be "idx" from Step.tsx, updated if so
                testContentId={step.testContentId!}
                readonly
                disabled
                editing={false}
                setEditing={() => void 0}
                disableMenu
                disableSelect
              />
              {!!operation.opensPageAlias && (
                <div className="flex gap-1 px-1">
                  <p className="text-xs opacity-50">opens </p>
                  <Badge
                    size="xs"
                    className="mx-1 min-w-fit lowercase"
                    color="gray"
                  >
                    {operation.opensPageAlias}
                  </Badge>
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 px-2">
            {!!showLangSmithDebug && (
              <Tooltip label="Debug AI action in LangSmith">
                <ActionIcon
                  size="xs"
                  variant="subtle"
                  radius="xl"
                  color="orange"
                  className="text-orange-500 hover:bg-orange-50 dark:hover:bg-orange-900/20"
                  onClick={async (event) => {
                    event.stopPropagation();
                    if (!operation?.id || !testRunAttemptId) {
                      return;
                    }
                    try {
                      const traceUrl = await getLangSmithAiTraceUrl({
                        executionId: testRunAttemptId,
                        stepId: step.id,
                        initiator: 'executor',
                        operationId: operation.id,
                      });
                      window.open(traceUrl, '_blank', 'noopener,noreferrer');
                    } catch (error) {
                      console.error('Failed to get LangSmith AI action trace URL:', error);
                    }
                  }}
                >
                  <IconBug size={16} />
                </ActionIcon>
              </Tooltip>
            )}
            {operation.actionScreenshot || operation.afterScreenshot ?
              <IconPhoto
                size={16}
                className="shrink-0 cursor-pointer text-gray-600 dark:text-stone-300"
                onClick={selectScreenshot}
              />
            : null}
          </div>
        </div>
        {!!hasAiActionLogs && (
          <div
            className={clsx('rounded-b-lg border-2 border-t-0 px-2 pb-1', {
              'bg-red-100 dark:bg-red-900/20': type === 'deleted',
              'bg-emerald-100 dark:bg-green-900/20': type === 'added',
              'border-blue-500': isSelected,
              'border-transparent': !isSelected,
            })}
          >
            <AiActionLogDisplay
              logs={filteredAiActionLogs}
              viewOnlyLabel
              selectedLogId={selectedAiLogId}
              onLogSelect={setSelectedAiLogId}
              selectOperationsForScreenshot={(params) => {
                setScreenshotDiff({
                  opened: Boolean(params.rightDirectScreenshots),
                  rightDirectScreenshots: params.rightDirectScreenshots,
                });
              }}
            />
          </div>
        )}
      </div>
    : <div className="h-full rounded-lg bg-zinc-100/50 dark:bg-zinc-700/30" />;
}
